/**
 * MaysCare theme scripts.
 *
 * One deferred file, no dependencies, no build step. Every behaviour is a web
 * component except the drawers, which are delegated from the document so that
 * markup rendered later (section reloads, cart re-renders) keeps working.
 */
(function () {
  'use strict';

  /* ==========================================================================
     Globals and helpers
     ========================================================================== */

  // layout/theme.liquid defines window.theme; layout/password.liquid does not,
  // so every read is defaulted rather than assumed.
  var theme = (window.theme = window.theme || {});
  theme.routes = theme.routes || {};
  theme.settings = theme.settings || {};
  theme.strings = theme.strings || {};

  var routes = theme.routes;
  var strings = theme.strings;

  var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  function reduceMotion() {
    return motionQuery.matches;
  }

  function isRTL() {
    return document.documentElement.getAttribute('dir') === 'rtl';
  }

  function define(name, constructor) {
    if (!window.customElements.get(name)) window.customElements.define(name, constructor);
  }

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var context = this;
      clearTimeout(timer);
      timer = setTimeout(() => {
        fn.apply(context, args);
      }, wait);
    };
  }

  function onMotionChange(handler) {
    if (typeof motionQuery.addEventListener === 'function') {
      motionQuery.addEventListener('change', handler);
      return () => {
        motionQuery.removeEventListener('change', handler);
      };
    }
    motionQuery.addListener(handler);
    return () => {
      motionQuery.removeListener(handler);
    };
  }

  /** Shopify needs the current path to render Section Rendering API responses in context. */
  function sectionsUrl() {
    return window.location.pathname;
  }

  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function fetchText(url, signal) {
    return fetch(url, { signal: signal, headers: { Accept: 'text/html' } }).then((response) => {
      if (!response.ok) throw new Error('Request failed with status ' + response.status);
      return response.text();
    });
  }

  function focusById(id) {
    if (!id) return false;
    var el = document.getElementById(id);
    if (!el) return false;
    el.focus({ preventScroll: true });
    return true;
  }

  function scrollToIn(container, element, smooth) {
    // Scrolling by the delta between two rects is the only direction-agnostic
    // way to do this: scrollLeft is signed differently across RTL engines.
    var delta = element.getBoundingClientRect().left - container.getBoundingClientRect().left;
    if (Math.abs(delta) < 1) return false;
    container.scrollBy({ left: delta, behavior: smooth && !reduceMotion() ? 'smooth' : 'auto' });
    return true;
  }

  function setMessage(target, message) {
    if (!target) return;
    if (message) {
      target.textContent = message;
      target.hidden = false;
    } else {
      target.textContent = '';
      target.hidden = true;
    }
  }

  /* ==========================================================================
     Drawers — <dialog class="drawer">
     ========================================================================== */

  var drawerOpeners = new WeakMap();
  var drawerReady = new WeakSet();
  var drawerClosing = new WeakSet();
  var scrollLocks = 0;

  function lockScroll() {
    if (scrollLocks === 0) {
      var width = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty('--scrollbar-width', width + 'px');
      document.documentElement.classList.add('drawer-open');
    }
    scrollLocks += 1;
  }

  function unlockScroll() {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks === 0) document.documentElement.classList.remove('drawer-open');
  }

  function afterTransition(el, done, fallback) {
    if (reduceMotion()) {
      done();
      return;
    }
    var finished = false;
    var timer;
    function finish() {
      if (finished) return;
      finished = true;
      el.removeEventListener('transitionend', handler);
      clearTimeout(timer);
      done();
    }
    function handler(event) {
      if (event.target === el) finish();
    }
    el.addEventListener('transitionend', handler);
    timer = setTimeout(finish, fallback || 450);
  }

  function initDrawer(dialog) {
    if (drawerReady.has(dialog)) return;
    drawerReady.add(dialog);

    // Esc fires `cancel`; intercept it so the drawer animates out like any
    // other close instead of disappearing instantly.
    dialog.addEventListener(
      'cancel',
      (event) => {
        event.preventDefault();
        closeDrawer(dialog);
      },
      true
    );

    // A click on the backdrop is reported with the dialog itself as the target.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDrawer(dialog);
    });

    dialog.addEventListener('close', () => {
      dialog.classList.remove('is-open');
      drawerClosing.delete(dialog);
      unlockScroll();
      var opener = drawerOpeners.get(dialog);
      drawerOpeners.delete(dialog);
      if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
    });
  }

  function initDrawers(root) {
    var scope = root || document;
    if (scope.matches && scope.matches('dialog.drawer')) initDrawer(scope);
    if (!scope.querySelectorAll) return;
    scope.querySelectorAll('dialog.drawer').forEach(initDrawer);
  }

  function openDrawer(id, opener) {
    var dialog = document.getElementById(id);
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    initDrawer(dialog);
    if (dialog.open) return true;
    if (opener) drawerOpeners.set(dialog, opener);
    dialog.showModal();
    lockScroll();
    requestAnimationFrame(() => {
      dialog.classList.add('is-open');
    });
    return true;
  }

  function closeDrawer(dialog) {
    if (!dialog || !dialog.open || drawerClosing.has(dialog)) return;
    drawerClosing.add(dialog);
    dialog.classList.remove('is-open');
    var panel = dialog.querySelector('.drawer__inner') || dialog;
    afterTransition(panel, () => {
      dialog.close();
    });
  }

  document.addEventListener('click', (event) => {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var opener = target.closest('[data-drawer-open]');
    if (opener) {
      // Openers are links where a no-JS fallback exists, so only swallow the
      // navigation once the drawer has actually opened.
      if (openDrawer(opener.getAttribute('data-drawer-open'), opener)) event.preventDefault();
      return;
    }

    var closer = target.closest('[data-drawer-close]');
    if (closer) {
      var dialog = closer.closest('dialog.drawer');
      if (dialog) {
        event.preventDefault();
        closeDrawer(dialog);
      }
    }
  });

  initDrawers(document);

  /* ==========================================================================
     Cart rendering — shared by <product-form> and <cart-items>
     ========================================================================== */

  function updateCartCount() {
    var inner = document.getElementById('CartDrawer-Inner');
    if (!inner) return;
    var count = parseInt(inner.getAttribute('data-cart-count'), 10) || 0;
    var label = inner.getAttribute('data-cart-count-label') || '';

    document.querySelectorAll('[data-cart-bubble]').forEach((bubble) => {
      bubble.textContent = count > 99 ? '99+' : String(count);
      bubble.hidden = count === 0;
    });

    document.querySelectorAll('[data-cart-count-label]').forEach((el) => {
      if (el === inner) return; // the drawer carries the label as data, not as text
      el.textContent = label;
    });
  }

  function renderCartSections(sections) {
    if (sections) {
      var drawerHTML = sections['cart-drawer'];
      if (drawerHTML) {
        var currentInner = document.getElementById('CartDrawer-Inner');
        var freshInner = parseHTML(drawerHTML).getElementById('CartDrawer-Inner');
        if (currentInner && freshInner) currentInner.replaceWith(document.importNode(freshInner, true));
      }

      var mainCart = document.querySelector('[data-main-cart]');
      if (mainCart) {
        var mainHTML = sections[mainCart.getAttribute('data-section-id')];
        if (mainHTML) {
          var freshMain = parseHTML(mainHTML).querySelector('[data-main-cart]');
          if (freshMain) mainCart.replaceWith(document.importNode(freshMain, true));
        }
      }
    }
    updateCartCount();
  }

  function cartSectionIds() {
    var ids = ['cart-drawer'];
    var mainCart = document.querySelector('[data-main-cart]');
    var mainId = mainCart && mainCart.getAttribute('data-section-id');
    if (mainId && ids.indexOf(mainId) === -1) ids.push(mainId);
    return ids;
  }

  function announceCartUpdate(detail) {
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: detail || {} }));
  }

  /* ==========================================================================
     <header-menu>
     ========================================================================== */

  class HeaderMenu extends HTMLElement {
    connectedCallback() {
      this.groups = Array.from(this.querySelectorAll('details.menu__details'));
      if (!this.groups.length) return;

      this.onToggle = this.onToggle.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      this.groups.forEach(
        (group) => {
          group.addEventListener('toggle', this.onToggle);
        }
      );
      document.addEventListener('click', this.onDocumentClick);
      this.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      if (!this.groups) return;
      this.groups.forEach(
        (group) => {
          group.removeEventListener('toggle', this.onToggle);
        }
      );
      document.removeEventListener('click', this.onDocumentClick);
      this.removeEventListener('keydown', this.onKeydown);
    }

    onToggle(event) {
      if (!event.target.open) return;
      this.closeAll(event.target);
    }

    closeAll(except) {
      this.groups.forEach((group) => {
        if (group !== except) group.open = false;
      });
    }

    onDocumentClick(event) {
      if (this.contains(event.target)) return;
      this.closeAll(null);
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      var open = this.groups.filter((group) => {
        return group.open;
      });
      if (!open.length) return;
      event.stopPropagation();
      this.closeAll(null);
      var summary = open[0].querySelector('summary');
      if (summary) summary.focus({ preventScroll: true });
    }
  }
  define('header-menu', HeaderMenu);

  /* ==========================================================================
     <announcement-bar>
     ========================================================================== */

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      if (this.ready) {
        this.attachGlobals();
        this.restart();
        return;
      }
      this.items = Array.from(this.querySelectorAll('[data-announcement]'));
      if (this.items.length < 2) return;
      this.ready = true;

      this.index = Math.max(
        0,
        this.items.findIndex((item) => {
          return !item.hidden;
        })
      );
      this.speed = (parseInt(this.getAttribute('data-speed'), 10) || 5) * 1000;
      this.paused = false;
      this.timer = null;

      this.onPointerEnter = this.pause.bind(this);
      this.onPointerLeave = this.resume.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      this.onMotion = this.restart.bind(this);

      var prev = this.querySelector('[data-prev]');
      var next = this.querySelector('[data-next]');
      if (prev) prev.addEventListener('click', this.step.bind(this, -1));
      if (next) next.addEventListener('click', this.step.bind(this, 1));

      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
      this.addEventListener('focusin', this.onPointerEnter);
      this.addEventListener('focusout', this.onPointerLeave);
      this.attachGlobals();

      this.restart();
    }

    disconnectedCallback() {
      this.stop();
      document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.stopMotionWatch) this.stopMotionWatch();
    }

    attachGlobals() {
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.addEventListener('visibilitychange', this.onVisibility);
      if (this.stopMotionWatch) this.stopMotionWatch();
      this.stopMotionWatch = onMotionChange(this.onMotion);
    }

    get autoplay() {
      return this.getAttribute('data-autoplay') === 'true' && !reduceMotion();
    }

    show(index) {
      var total = this.items.length;
      this.index = ((index % total) + total) % total;
      this.items.forEach(
        (item, i) => {
          item.hidden = i !== this.index;
        }
      );
    }

    step(direction, event) {
      if (event) event.preventDefault();
      this.show(this.index + direction);
      this.restart();
    }

    restart() {
      this.stop();
      if (!this.autoplay || this.paused || document.hidden) return;
      this.timer = setInterval(
        () => {
          this.show(this.index + 1);
        },
        this.speed
      );
    }

    stop() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }

    pause() {
      this.paused = true;
      this.stop();
    }

    resume() {
      this.paused = false;
      this.restart();
    }

    onVisibility() {
      if (document.hidden) this.stop();
      else this.restart();
    }
  }
  define('announcement-bar', AnnouncementBar);

  /* ==========================================================================
     <slideshow-component>
     ========================================================================== */

  class SlideshowComponent extends HTMLElement {
    connectedCallback() {
      if (this.ready) {
        this.attachGlobals();
        this.restart();
        return;
      }
      this.track = this.querySelector('[data-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      if (!this.track || this.slides.length < 2) return;
      this.ready = true;

      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      this.toggleButton = this.querySelector('[data-toggle]');
      this.index = 0;
      this.speed = (parseInt(this.getAttribute('data-speed'), 10) || 6) * 1000;
      this.paused = false;
      this.timer = null;

      var prev = this.querySelector('[data-prev]');
      var next = this.querySelector('[data-next]');
      if (prev) prev.addEventListener('click', this.step.bind(this, -1));
      if (next) next.addEventListener('click', this.step.bind(this, 1));

      this.dots.forEach(
        (dot, i) => {
          dot.addEventListener(
            'click',
            () => {
              this.showSlide(i);
              this.restart();
            }
          );
        }
      );

      if (this.toggleButton) this.toggleButton.addEventListener('click', this.toggle.bind(this));

      this.onPointerEnter = this.pause.bind(this);
      this.onPointerLeave = this.resume.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      this.onMotion = this.restart.bind(this);

      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
      this.addEventListener('focusin', this.onPointerEnter);
      this.addEventListener('focusout', this.onPointerLeave);

      this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
        root: this.track,
        threshold: 0.6
      });
      this.attachGlobals();

      this.setActive(0);
      this.restart();
    }

    disconnectedCallback() {
      this.stop();
      if (this.observer) this.observer.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.stopMotionWatch) this.stopMotionWatch();
    }

    attachGlobals() {
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.addEventListener('visibilitychange', this.onVisibility);
      if (this.stopMotionWatch) this.stopMotionWatch();
      this.stopMotionWatch = onMotionChange(this.onMotion);
      if (this.observer) {
        this.slides.forEach(
          (slide) => {
            this.observer.observe(slide);
          }
        );
      }
    }

    get autoplay() {
      return this.getAttribute('data-autoplay') === 'true' && !reduceMotion();
    }

    onIntersect(entries) {
      entries.forEach(
        (entry) => {
          if (!entry.isIntersecting) return;
          this.setActive(this.slides.indexOf(entry.target));
        }
      );
    }

    setActive(index) {
      if (index < 0) return;
      this.index = index;
      this.dots.forEach((dot, i) => {
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    showSlide(index, smooth) {
      var total = this.slides.length;
      var next = ((index % total) + total) % total;
      var slide = this.slides[next];
      if (!slide) return;
      if (!scrollToIn(this.track, slide, smooth !== false)) this.setActive(next);
    }

    /** Called by the theme editor when a slide block is selected. */
    showSlideElement(element) {
      var index = this.slides.indexOf(element);
      if (index > -1) {
        this.pause();
        this.showSlide(index, false);
      }
    }

    step(direction, event) {
      if (event) event.preventDefault();
      this.showSlide(this.index + direction);
      this.restart();
    }

    toggle() {
      if (!this.toggleButton) return;
      var pressed = this.toggleButton.getAttribute('aria-pressed') === 'true';
      this.toggleButton.setAttribute('aria-pressed', pressed ? 'false' : 'true');
      this.userPaused = !pressed;
      if (this.userPaused) this.stop();
      else this.restart();
    }

    restart() {
      this.stop();
      if (!this.autoplay || this.paused || this.userPaused || document.hidden) return;
      this.timer = setInterval(
        () => {
          this.showSlide(this.index + 1);
        },
        this.speed
      );
    }

    stop() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }

    pause() {
      this.paused = true;
      this.stop();
    }

    resume() {
      this.paused = false;
      this.restart();
    }

    onVisibility() {
      if (document.hidden) this.stop();
      else this.restart();
    }
  }
  define('slideshow-component', SlideshowComponent);

  /* ==========================================================================
     <tabs-component>
     ========================================================================== */

  class TabsComponent extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      if (this.tabs.length < 2) return;
      this.ready = true;

      this.panels = this.tabs.map((tab) => {
        return document.getElementById(tab.getAttribute('aria-controls'));
      });

      this.tabs.forEach(
        (tab, index) => {
          tab.addEventListener(
            'click',
            () => {
              this.select(index, false);
            }
          );
          tab.addEventListener('keydown', this.onKeydown.bind(this, index));
        }
      );
    }

    onKeydown(index, event) {
      var last = this.tabs.length - 1;
      var forward = isRTL() ? 'ArrowLeft' : 'ArrowRight';
      var back = isRTL() ? 'ArrowRight' : 'ArrowLeft';
      var next = null;

      if (event.key === forward) next = index === last ? 0 : index + 1;
      else if (event.key === back) next = index === 0 ? last : index - 1;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = last;
      else return;

      event.preventDefault();
      this.select(next, true);
    }

    select(index, focus) {
      this.tabs.forEach(
        (tab, i) => {
          var selected = i === index;
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          tab.setAttribute('tabindex', selected ? '0' : '-1');
          var panel = this.panels[i];
          if (panel) panel.hidden = !selected;
        }
      );
      if (focus) this.tabs[index].focus({ preventScroll: true });
    }
  }
  define('tabs-component', TabsComponent);

  /* ==========================================================================
     <quantity-input>
     ========================================================================== */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.ready = true;
      this.addEventListener('click', this.onClick.bind(this));
    }

    onClick(event) {
      var button = event.target.closest('button[name="minus"], button[name="plus"]');
      if (!button || !this.contains(button)) return;
      event.preventDefault();

      var before = this.input.value;
      var up = button.name === 'plus';
      try {
        if (up) this.input.stepUp();
        else this.input.stepDown();
      } catch (error) {
        this.stepManually(up);
      }
      if (this.input.value !== before) {
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    stepManually(up) {
      var step = parseFloat(this.input.step) || 1;
      var min = this.input.min === '' ? -Infinity : parseFloat(this.input.min);
      var max = this.input.max === '' ? Infinity : parseFloat(this.input.max);
      var value = parseFloat(this.input.value);
      if (!isFinite(value)) value = isFinite(min) ? min : 0;
      value = up ? value + step : value - step;
      this.input.value = String(Math.min(max, Math.max(min, value)));
    }
  }
  define('quantity-input', QuantityInput);

  /* ==========================================================================
     <product-form>
     ========================================================================== */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.ready = true;
      this.errorTarget = this.querySelector('[data-form-error]');
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.loading = false;
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    onSubmit(event) {
      // Anything other than the drawer keeps the native POST to /cart/add.
      if (theme.settings.cartType !== 'drawer') return;
      event.preventDefault();
      if (this.loading) return;

      setMessage(this.errorTarget, '');
      this.setLoading(true);

      var body = new FormData(this.form);
      body.append('sections', cartSectionIds().join(','));
      body.append('sections_url', sectionsUrl());

      fetch(routes.cartAdd + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: body
      })
        .then((response) => {
          return response.json().catch(() => {
            return { status: response.status };
          });
        })
        .then(
          (data) => {
            if (data && data.status) {
              setMessage(this.errorTarget, data.description || data.message || strings.cartError);
              return;
            }
            renderCartSections(data && data.sections);
            openDrawer('CartDrawer', this.submitButton);
            announceCartUpdate({ source: 'product-form', response: data });
          }
        )
        .catch(
          () => {
            setMessage(this.errorTarget, strings.cartError);
          }
        )
        .finally(
          () => {
            this.setLoading(false);
          }
        );
    }

    setLoading(loading) {
      this.loading = loading;
      if (!this.submitButton) return;
      this.submitButton.classList.toggle('is-loading', loading);
      if (loading) this.submitButton.setAttribute('aria-disabled', 'true');
      else this.submitButton.removeAttribute('aria-disabled');
    }
  }
  define('product-form', ProductForm);

  /* ==========================================================================
     <cart-items>
     ========================================================================== */

  class CartItems extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.errorTarget = this.querySelector('[data-cart-error]');
      this.onChange = debounce(this.handleChange.bind(this), 300);
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.handleClick.bind(this));
    }

    handleChange(event) {
      var input = event.target.closest('input[data-line]');
      if (!input || !this.contains(input)) return;
      var quantity = parseInt(input.value, 10);
      if (!isFinite(quantity) || quantity < 0) return;
      this.updateLine(parseInt(input.getAttribute('data-line'), 10), quantity, input.id);
    }

    handleClick(event) {
      var button = event.target.closest('[data-remove][data-line]');
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      this.updateLine(parseInt(button.getAttribute('data-line'), 10), 0, button.id);
    }

    updateLine(line, quantity, focusId) {
      if (!isFinite(line)) return;
      setMessage(this.errorTarget, '');
      this.setBusy(true);

      fetch(routes.cartChange + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          line: line,
          quantity: quantity,
          sections: cartSectionIds(),
          sections_url: sectionsUrl()
        })
      })
        .then((response) => {
          return response.json().then((data) => {
            return { ok: response.ok, data: data };
          });
        })
        .then(
          (result) => {
            if (!result.ok || (result.data && result.data.status)) {
              this.setBusy(false);
              setMessage(
                this.errorTarget,
                (result.data && (result.data.description || result.data.message)) || strings.cartError
              );
              return;
            }
            renderCartSections(result.data.sections);
            restoreCartFocus(focusId, line);
            announceCartUpdate({ source: 'cart-items', cart: result.data });
          }
        )
        .catch(
          () => {
            this.setBusy(false);
            setMessage(this.errorTarget, strings.cartError);
          }
        );
    }

    setBusy(busy) {
      this.classList.toggle('is-busy', busy);
      if (busy) this.setAttribute('aria-busy', 'true');
      else this.removeAttribute('aria-busy');
    }
  }
  define('cart-items', CartItems);

  /**
   * Cart markup is replaced wholesale, so focus is restored by id. When the
   * line itself is gone, fall back to the nearest still-present control.
   */
  function restoreCartFocus(id, line) {
    if (!id) return;
    if (focusById(id)) return;

    var prefix = id.split('-')[0];
    var candidates = [
      prefix + '-Remove-' + line,
      prefix + '-Remove-' + (line - 1),
      prefix + '-Remove-1'
    ];
    for (var i = 0; i < candidates.length; i += 1) {
      if (focusById(candidates[i])) return;
    }

    var drawer = document.getElementById('CartDrawer');
    if (prefix === 'Drawer' && drawer && drawer.open) {
      var close = drawer.querySelector('[data-drawer-close]');
      if (close) {
        close.focus({ preventScroll: true });
        return;
      }
    }

    var heading = document.querySelector('[data-main-cart] .page-title');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }
  }

  /* ==========================================================================
     <variant-picker>
     ========================================================================== */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.getAttribute('data-section-id');
      this.productUrl = this.getAttribute('data-url');
      if (this.ready) return;
      this.ready = true;
      this.addEventListener('change', this.onChange.bind(this));
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
    }

    onChange() {
      var ids = Array.from(
        this.querySelectorAll('input[data-option-value-id]:checked'),
        (input) => {
          return input.getAttribute('data-option-value-id');
        }
      );
      if (!ids.length || !this.productUrl) return;

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.setAttribute('aria-busy', 'true');

      var url =
        this.productUrl +
        '?option_values=' +
        encodeURIComponent(ids.join(',')) +
        '&section_id=' +
        encodeURIComponent(this.sectionId);

      fetchText(url, this.controller.signal)
        .then(
          (html) => {
            this.apply(parseHTML(html));
          }
        )
        .catch((error) => {
          if (error.name !== 'AbortError') console.error(error);
        })
        .finally(
          () => {
            this.removeAttribute('aria-busy');
          }
        );
    }

    apply(doc) {
      var sectionId = this.sectionId;
      var active = document.activeElement;
      var activeId = active && active.id;

      ['VariantPicker-', 'Price-', 'Sku-', 'Inventory-', 'StickyAtc-'].forEach(function (prefix) {
        var id = prefix + sectionId;
        var current = document.getElementById(id);
        var fresh = doc.getElementById(id);
        // innerHTML rather than replaceWith: it keeps this element (and its
        // listeners) alive and leaves <sticky-atc> visibility state untouched.
        if (current && fresh) current.innerHTML = fresh.innerHTML;
      });

      var info = doc.querySelector('[data-product-info]');
      var variantId = info ? info.getAttribute('data-variant-id') : '';
      var mediaId = info ? info.getAttribute('data-featured-media-id') : '';

      var liveInfo = document.querySelector('[data-product-info="' + sectionId + '"]');
      if (liveInfo && info) {
        liveInfo.setAttribute('data-variant-id', variantId || '');
        liveInfo.setAttribute('data-featured-media-id', mediaId || '');
      }

      var form = document.getElementById('ProductForm-' + sectionId);
      if (form) {
        var idInput = form.querySelector('input[name="id"]');
        if (idInput) {
          idInput.value = variantId || '';
          idInput.disabled = !variantId;
        }
      }

      // The add button is inside the form, which is never re-rendered — the
      // payment button must keep its own state.
      var button = document.getElementById('AddToCart-' + sectionId);
      var freshButton = doc.getElementById('AddToCart-' + sectionId);
      if (button && freshButton) {
        button.innerHTML = freshButton.innerHTML;
        button.disabled = freshButton.disabled;
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
      }

      try {
        var url = new URL(window.location.href);
        if (variantId) url.searchParams.set('variant', variantId);
        else url.searchParams.delete('variant');
        window.history.replaceState({}, '', url.toString());
      } catch (error) {
        /* history is best-effort */
      }

      if (mediaId) {
        var gallery = document.querySelector('product-gallery[data-section-id="' + sectionId + '"]');
        if (gallery && typeof gallery.goToMedia === 'function') gallery.goToMedia(mediaId);
      }

      focusById(activeId);
    }
  }
  define('variant-picker', VariantPicker);

  /* ==========================================================================
     <product-gallery>
     ========================================================================== */

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      if (this.ready) {
        this.observeItems();
        return;
      }
      this.viewport = this.querySelector('[data-gallery-viewport]');
      this.items = Array.from(this.querySelectorAll('[data-gallery-item]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      if (!this.viewport || !this.items.length) return;
      this.ready = true;

      this.index = 0;
      this.prevButton = this.querySelector('[data-gallery-prev]');
      this.nextButton = this.querySelector('[data-gallery-next]');
      if (this.prevButton) this.prevButton.addEventListener('click', this.step.bind(this, -1));
      if (this.nextButton) this.nextButton.addEventListener('click', this.step.bind(this, 1));

      this.thumbs.forEach(
        (thumb) => {
          thumb.addEventListener(
            'click',
            () => {
              this.goToMedia(thumb.getAttribute('data-thumb'));
            }
          );
        }
      );

      this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
        root: this.viewport,
        threshold: 0.6
      });
      this.observeItems();

      var initial = this.getAttribute('data-initial-media');
      requestAnimationFrame(
        () => {
          if (initial) this.goToMedia(initial, false);
          else this.setActive(0);
        }
      );
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    observeItems() {
      if (!this.observer) return;
      this.items.forEach(
        (item) => {
          this.observer.observe(item);
        }
      );
    }

    onIntersect(entries) {
      entries.forEach(
        (entry) => {
          if (entry.isIntersecting) {
            this.setActive(this.items.indexOf(entry.target));
          } else {
            pauseMedia(entry.target);
          }
        }
      );
    }

    setActive(index) {
      if (index < 0) return;
      this.index = index;
      var mediaId = this.items[index].getAttribute('data-media-id');
      this.thumbs.forEach((thumb) => {
        if (thumb.getAttribute('data-thumb') === mediaId) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      if (this.prevButton) this.prevButton.disabled = index === 0;
      if (this.nextButton) this.nextButton.disabled = index === this.items.length - 1;
    }

    step(direction, event) {
      if (event) event.preventDefault();
      var next = Math.min(this.items.length - 1, Math.max(0, this.index + direction));
      this.goToIndex(next, true);
    }

    goToIndex(index, smooth) {
      var item = this.items[index];
      if (!item) return;
      if (!scrollToIn(this.viewport, item, smooth !== false)) this.setActive(index);
    }

    /** Public: used by <variant-picker> when the featured media changes. */
    goToMedia(id, smooth) {
      var target = String(id);
      var index = this.items.findIndex((item) => {
        return item.getAttribute('data-media-id') === target;
      });
      if (index < 0) return;
      this.goToIndex(index, smooth);
    }
  }
  define('product-gallery', ProductGallery);

  function pauseMedia(container) {
    container.querySelectorAll('video').forEach((video) => {
      if (!video.paused) video.pause();
    });
    container.querySelectorAll('iframe').forEach((iframe) => {
      var src = iframe.getAttribute('src') || '';
      try {
        if (src.indexOf('youtube') > -1) {
          iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        } else if (src.indexOf('vimeo') > -1) {
          iframe.contentWindow.postMessage('{"method":"pause"}', '*');
        }
      } catch (error) {
        /* cross-origin frames that do not expose an API */
      }
    });
  }

  /* ==========================================================================
     <product-recommendations>
     ========================================================================== */

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.loaded) return;
      var url = this.getAttribute('data-url');
      if (!url) return;
      if (this.observer) this.observer.disconnect();

      var observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          this.load(url);
        },
        { rootMargin: '400px' }
      );
      observer.observe(this);
      this.observer = observer;
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    load(url) {
      fetchText(url)
        .then(
          (html) => {
            this.loaded = true;
            var fresh = parseHTML(html).querySelector('product-recommendations');
            if (fresh) this.innerHTML = fresh.innerHTML;
            if (!this.querySelector('.card')) this.hidden = true;
          }
        )
        .catch(
          () => {
            this.loaded = true;
            this.hidden = true;
          }
        );
    }
  }
  define('product-recommendations', ProductRecommendations);

  /* ==========================================================================
     <facet-filters>
     ========================================================================== */

  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.getAttribute('data-section-id');
      this.baseUrl = this.getAttribute('data-url') || window.location.pathname;
      if (this.ready) {
        window.addEventListener('popstate', this.onPopState);
        return;
      }
      this.ready = true;

      this.onChange = debounce(this.handleChange.bind(this), 350);
      this.onPopState = this.handlePopState.bind(this);

      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('submit', this.handleSubmit.bind(this));
      window.addEventListener('popstate', this.onPopState);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      if (this.controller) this.controller.abort();
    }

    get basePath() {
      try {
        return new URL(this.baseUrl, window.location.origin).pathname;
      } catch (error) {
        return this.baseUrl;
      }
    }

    collectParams() {
      var params = new URLSearchParams();
      var form = this.querySelector('[data-facets-form]');

      if (form) {
        var data = new FormData(form);
        data.forEach((value, key) => {
          if (typeof value !== 'string' || value.trim() === '') return;
          params.append(key, value);
        });
      } else {
        // No filters on this page: the sort select is not form-associated, so
        // the current query string is the only source of the other params.
        new URLSearchParams(window.location.search).forEach((value, key) => {
          if (value === '') return;
          params.append(key, value);
        });
      }

      var sort = this.querySelector('select[data-sort]');
      if (sort && sort.value) params.set('sort_by', sort.value);
      params.delete('page');
      return params;
    }

    handleChange(event) {
      if (!event.target.closest('[data-facets-form], select[data-sort]')) return;
      this.apply(this.basePath, this.collectParams(), true);
    }

    handleSubmit(event) {
      var form = event.target.closest('[data-facets-form]');
      if (!form) return;
      // [data-facets-apply] has nothing left to do once JS is running: the
      // results are already live, so it only dismisses the drawer.
      event.preventDefault();
      var dialog = form.closest('dialog.drawer');
      if (dialog) closeDrawer(dialog);
      this.apply(this.basePath, this.collectParams(), true);
    }

    handleClick(event) {
      var link = event.target.closest('[data-facet-link]');
      if (!link || !this.contains(link)) return;
      event.preventDefault();
      var url;
      try {
        url = new URL(link.href, window.location.origin);
      } catch (error) {
        return;
      }
      var params = new URLSearchParams(url.search);
      params.delete('page');
      this.apply(url.pathname, params, true);
    }

    handlePopState() {
      var params = new URLSearchParams(window.location.search);
      this.apply(window.location.pathname, params, false);
    }

    apply(path, params, push) {
      var query = params.toString();
      var target = path + (query ? '?' + query : '');
      var fetchUrl = path + '?' + (query ? query + '&' : '') + 'section_id=' + encodeURIComponent(this.sectionId);

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.setAttribute('aria-busy', 'true');

      fetchText(fetchUrl, this.controller.signal)
        .then(
          (html) => {
            this.render(parseHTML(html));
            if (push) window.history.pushState({ facets: true }, '', target);
          }
        )
        .catch((error) => {
          if (error.name !== 'AbortError') console.error(error);
        })
        .finally(
          () => {
            this.removeAttribute('aria-busy');
          }
        );
    }

    render(doc) {
      var openFacets = Array.from(this.querySelectorAll('.facet[open]'), (facet) => {
        return facet.id;
      });
      var active = document.activeElement;
      var activeId = active && active.id;

      ['[data-results]', '[data-active-filters]', '[data-facets-body]', '[data-results-count]'].forEach(
        (selector) => {
          var current = this.querySelector(selector);
          var fresh = doc.querySelector(selector);
          if (current && fresh) current.innerHTML = fresh.innerHTML;
        }
      );

      this.querySelectorAll('.facet').forEach((facet) => {
        if (openFacets.indexOf(facet.id) > -1) facet.open = true;
      });

      focusById(activeId);
    }
  }
  define('facet-filters', FacetFilters);

  /* ==========================================================================
     <localization-form>
     ========================================================================== */

  class LocalizationForm extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.addEventListener('change', function (event) {
        if (!event.target.matches('select')) return;
        var form = event.target.form || this.querySelector('form');
        if (form) form.submit();
      });
    }
  }
  define('localization-form', LocalizationForm);

  /* ==========================================================================
     <delivery-estimate>
     ========================================================================== */

  function addBusinessDays(start, days, skip) {
    var skipDays = skip === 'fri_sat' ? [5, 6] : skip === 'sat_sun' ? [6, 0] : [];
    var date = new Date(start.getTime());
    var added = 0;
    var guard = 0;
    while (added < days && guard < 400) {
      date.setDate(date.getDate() + 1);
      guard += 1;
      if (skipDays.indexOf(date.getDay()) === -1) added += 1;
    }
    return date;
  }

  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      var target = this.querySelector('[data-dates]');
      if (!target) return;
      this.ready = true;

      var min = parseInt(this.getAttribute('data-min'), 10);
      var max = parseInt(this.getAttribute('data-max'), 10);
      if (!isFinite(min) || !isFinite(max)) return;
      if (max < min) max = min;

      var skip = this.getAttribute('data-skip') || 'none';
      var today = new Date();
      var from = addBusinessDays(today, min, skip);
      var to = addBusinessDays(today, max, skip);

      try {
        var formatter = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
          weekday: 'short',
          month: 'short',
          day: 'numeric'
        });
        if (from.getTime() === to.getTime()) {
          target.textContent = formatter.format(from);
        } else if (typeof formatter.formatRange === 'function') {
          target.textContent = formatter.formatRange(from, to);
        } else {
          target.textContent = formatter.format(from) + ' – ' + formatter.format(to);
        }
      } catch (error) {
        /* leave the Liquid fallback in place */
      }
    }
  }
  define('delivery-estimate', DeliveryEstimate);

  /* ==========================================================================
     <sticky-atc>
     ========================================================================== */

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      var targetId = this.getAttribute('data-target');
      var target = targetId && document.getElementById(targetId);
      if (!target) return;
      if (this.observer) {
        this.observer.observe(target);
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(
            (entry) => {
              var rootTop = entry.rootBounds ? entry.rootBounds.top : 0;
              // Only once the real button has scrolled past the top — never
              // while it is still below the fold.
              var isAbove = !entry.isIntersecting && entry.boundingClientRect.bottom <= rootTop;
              this.setVisible(isAbove);
            }
          );
        },
        { threshold: 0 }
      );
      this.observer.observe(target);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    setVisible(visible) {
      this.classList.toggle('is-visible', visible);
      if (visible) this.removeAttribute('inert');
      else this.setAttribute('inert', '');
    }
  }
  define('sticky-atc', StickyAtc);

  /* ==========================================================================
     Theme editor
     ========================================================================== */

  document.addEventListener('shopify:section:load', (event) => {
    initDrawers(event.target);
  });

  document.addEventListener('shopify:section:select', (event) => {
    if (event.target.querySelector('#CartDrawer')) openDrawer('CartDrawer', null);
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    var drawer = event.target.querySelector('#CartDrawer');
    if (drawer) closeDrawer(drawer);
  });

  document.addEventListener('shopify:block:select', (event) => {
    var slideshow = event.target.closest('slideshow-component');
    if (slideshow && typeof slideshow.showSlideElement === 'function') {
      slideshow.showSlideElement(event.target);
    }
  });
})();
